// Dictée vocale commune à tous les champs texte de la Clinique (04/10/2026).
// - Traitement sur l'appareil (processLocally), comme la synthèse : aucun audio du dossier n'est transmis.
// - Mise en forme : espace entre deux dictées, majuscule en début de phrase, ponctuation dictée
//   (« virgule », « point », « point à la ligne », « à la ligne », « nouveau paragraphe », « deux points »,
//   « point virgule », « point d'interrogation », « point d'exclamation », « ouvrez / fermez la parenthèse »).
// - Reprise automatique après un silence, jusqu'à « Arrêter la dictée ».
// - Insertion à l'emplacement du curseur : zone de texte, champ texte, éditeur riche (Quill) ou contenteditable.

const APOS = "['’]"
// Commandes prononcées : remplacées par leur signe (ordre important : expressions longues d'abord).
const COMMANDES = [
    [new RegExp('(^|\\s)point à la ligne(?=\\s|$)', 'gi'), '$1.¶'],
    [/(^|\s)nouveau paragraphe(?=\s|$)/gi, '$1¶¶'],
    [/(^|\s)(?:retour )?à la ligne(?=\s|$)/gi, '$1¶'],
    [/(^|\s)point[- ]virgule(?=\s|$)/gi, '$1;'],
    [new RegExp('(^|\\s)point d' + APOS + 'interrogation(?=\\s|$)', 'gi'), '$1?'],
    [new RegExp('(^|\\s)point d' + APOS + 'exclamation(?=\\s|$)', 'gi'), '$1!'],
    [/(^|\s)virgule(?=\s|$)/gi, '$1,'],
    [/(^|\s)(?:ouvre[zr]?|ouvrir) (?:la )?parenthèse(?=\s|$)/gi, '$1('],
    [/(^|\s)(?:ferme[zr]?|fermer) (?:la )?parenthèse(?=\s|$)/gi, '$1)'],
    // « deux points » et « point » ne valent ponctuation qu'en fin de dictée (« deux points de suture » reste du texte).
    [/(^|\s)deux points$/i, '$1:'],
    [/(^|\s)point$/i, '$1.'],
]

function finDePhrase(avant) {
    const t = String(avant || '').replace(/[ \t]+$/, '')
    return !t || /[.?!\n¶]$/.test(t)
}

function majusculeApresPhrase(texte) {
    return texte.replace(/([.?!]\s+|\n+)([a-zà-ÿ])/g, (m, sep, lettre) => sep + lettre.toUpperCase())
}

// Texte dicté (segment final) mis en forme selon ce qui précède le curseur.
export function formaterDictee(brut, avant) {
    let t = String(brut || '').replace(/\s+/g, ' ').trim()
    if (!t) return ''
    COMMANDES.forEach(([motif, signe]) => { t = t.replace(motif, signe) })
    t = t
        .replace(/\s+([,.)])/g, '$1')            // pas d'espace avant « , . ) »
        .replace(/\(\s+/g, '(')                   // ni après « ( »
        .replace(/\s*([;:?!])/g, ' $1')           // espace avant « ; : ? ! » (typographie française)
        .replace(/([,.;:?!)])(?=[^\s¶,.;:?!)])/g, '$1 ') // espace après la ponctuation
        .replace(/\s*¶\s*/g, '\n')
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/^[ \t]+|[ \t]+$/g, '')
    if (!t) return ''
    if (finDePhrase(avant)) t = t.replace(/^[a-zà-ÿ]/, lettre => lettre.toUpperCase())
    t = majusculeApresPhrase(t)
    const precedent = String(avant || '').slice(-1)
    const collerSansEspace = !precedent || /[\s(\n]/.test(precedent) || /^[,.)\n]/.test(t)
    return (collerSansEspace ? '' : ' ') + t
}

function quillDe(el) {
    if (!el) return null
    for (let n = el; n && n.nodeType === 1; n = n.parentNode) {
        if (n.__quill) return n.__quill
        if (n.classList && n.classList.contains('ql-container')) return null
    }
    return null
}

// Le champ réellement éditable : l'élément lui-même ou le premier champ qu'il contient.
export function champEditable(el) {
    if (!el) return null
    if (quillDe(el)) return el
    if (el.matches && el.matches('textarea, input, [contenteditable="true"], .ql-editor')) return el
    return el.querySelector ? el.querySelector('textarea, input[type="text"], input:not([type]), .ql-editor, [contenteditable="true"]') : null
}

// Insère le texte dicté au curseur et prévient Vue (événement input) pour mettre à jour v-model.
export function insererDictee(cible, brut) {
    const el = champEditable(cible)
    if (!el || !String(brut || '').trim()) return ''
    const quill = quillDe(el)
    if (quill) {
        const selection = quill.getSelection()
        const position = selection ? selection.index : Math.max(0, quill.getLength() - 1)
        const texte = formaterDictee(brut, quill.getText(0, position))
        if (!texte) return ''
        quill.insertText(position, texte, 'user')
        quill.setSelection(position + texte.length, 0, 'silent')
        return texte
    }
    if (el.tagName !== 'TEXTAREA' && el.tagName !== 'INPUT') {
        el.focus()
        const texte = formaterDictee(brut, el.innerText || '')
        if (texte && document.execCommand) document.execCommand('insertText', false, texte)
        return texte
    }
    const valeur = String(el.value || '')
    const debut = typeof el.selectionStart === 'number' ? el.selectionStart : valeur.length
    const fin = typeof el.selectionEnd === 'number' ? el.selectionEnd : debut
    let texte = formaterDictee(brut, valeur.slice(0, debut))
    if (el.tagName !== 'TEXTAREA') texte = texte.replace(/\n+/g, ' ')
    if (!texte) return ''
    el.value = valeur.slice(0, debut) + texte + valeur.slice(fin)
    const curseur = debut + texte.length
    try { el.setSelectionRange(curseur, curseur) } catch (_) { /* champ sans sélection */ }
    el.dispatchEvent(new Event('input', { bubbles: true }))
    return texte
}

const MESSAGES = {
    'not-allowed': 'Accès au microphone refusé. Autorisez le microphone dans le navigateur.',
    'service-not-allowed': 'Accès au microphone refusé. Autorisez le microphone dans le navigateur.',
    'audio-capture': 'Aucun microphone utilisable n’a été détecté.',
    'language-not-supported': 'Le module local de dictée française n’est pas installé.',
}

let dicteeEnCours = null

// Moteur : une seule dictée à la fois dans l'application ; démarrer ailleurs arrête la précédente.
export function creerDictee({ surFinal, surApercu, surEtat, surMessage } = {}) {
    const rappel = (fn, valeur) => { if (typeof fn === 'function') fn(valeur) }
    let reconnaissance = null
    let actif = false
    let tentative = 0
    let relances = 0
    let Reconnaissance = null

    function lancer() {
        reconnaissance = new Reconnaissance()
        reconnaissance.continuous = true
        reconnaissance.interimResults = true
        reconnaissance.lang = 'fr-FR'
        reconnaissance.processLocally = true
        reconnaissance.onresult = evt => {
            relances = 0
            let apercu = ''
            for (let i = evt.resultIndex; i < evt.results.length; ++i) {
                const resultat = evt.results[i]
                if (resultat.isFinal) rappel(surFinal, resultat[0].transcript)
                else apercu += resultat[0].transcript
            }
            rappel(surApercu, apercu.trim())
        }
        reconnaissance.onerror = evt => {
            if (MESSAGES[evt.error]) {
                actif = false
                rappel(surMessage, MESSAGES[evt.error])
            }
        }
        // Un silence termine la session : elle est relancée tant que l'utilisateur n'a pas arrêté.
        reconnaissance.onend = () => {
            if (actif && relances < 20) {
                relances++
                try { lancer(); return } catch (_) { /* arrêt ci-dessous */ }
            }
            actif = false
            if (dicteeEnCours === controle) dicteeEnCours = null
            rappel(surApercu, '')
            rappel(surEtat, false)
        }
        reconnaissance.start()
    }

    const controle = {
        get actif() { return actif },
        async demarrer() {
            if (dicteeEnCours && dicteeEnCours !== controle) dicteeEnCours.arreter()
            rappel(surMessage, '')
            Reconnaissance = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null
            if (!Reconnaissance) { rappel(surMessage, 'La dictée vocale n’est pas disponible dans ce navigateur. Vous pouvez continuer au clavier.'); return false }
            if (typeof Reconnaissance.available !== 'function' || typeof Reconnaissance.install !== 'function') {
                rappel(surMessage, 'La dictée locale n’est pas disponible dans ce navigateur. Aucun audio du dossier n’a été transmis.')
                return false
            }
            const essai = ++tentative
            const options = { langs: ['fr-FR'], processLocally: true }
            try {
                let disponibilite = await Reconnaissance.available(options)
                if (disponibilite === 'downloadable' || disponibilite === 'downloading') {
                    rappel(surMessage, 'Installation du module de dictée française sur cet appareil…')
                    disponibilite = (await Reconnaissance.install(options)) ? 'available' : 'unavailable'
                }
                if (essai !== tentative) return false
                if (disponibilite !== 'available') {
                    rappel(surMessage, 'La dictée française locale est indisponible sur cet appareil. Aucun audio n’a été transmis.')
                    return false
                }
            } catch (_) {
                if (essai === tentative) rappel(surMessage, 'Impossible d’activer la dictée locale. Vérifiez les autorisations du navigateur.')
                return false
            }
            try {
                actif = true
                relances = 0
                dicteeEnCours = controle
                lancer()
                rappel(surMessage, '')
                rappel(surEtat, true)
                return true
            } catch (_) {
                actif = false
                rappel(surMessage, 'Impossible de démarrer la dictée. Vérifiez l’autorisation du microphone.')
                return false
            }
        },
        arreter() {
            tentative++
            actif = false
            if (dicteeEnCours === controle) dicteeEnCours = null
            if (reconnaissance) { try { reconnaissance.stop() } catch (_) { /* déjà arrêtée */ } }
            rappel(surApercu, '')
            rappel(surEtat, false)
        },
    }
    return controle
}

// Champs dotés de leur propre bouton de dictée : le bouton flottant ne s'y affiche pas.
export const champsAvecBouton = new Set()
